import type { ItemVariation, MindItem, Product } from '@kimuno/api-sdk';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CollectionContentAdded, CollectionContentRemoved } from '../../ampli';
import type { ModalCollectionItem } from '../../constants';
import { selectUser } from '../../reducers/user.selectors';
import services from '../../services';
import { ManageItemInCollectionContext } from '../../state/ManageItemInCollectionProvider';
import { getAmpliContentEventPayload, getSearchIdWithFallback } from '../../utils/ampli';
import type { Alert } from '../NotificationAlert/NotificationAlert';
import AddToCollectionModal from './AddToCollectionModal';

interface ManageItemInCollectionModalProps {
	onClose: () => void;
	itemId: string;
	itemType: ItemVariation;
	show: boolean;
	itemData: Partial<MindItem> | Partial<Product> | undefined;
	onShowNotification?: (alert: Alert) => void;
}

const ManageItemInCollectionModal: React.FC<ManageItemInCollectionModalProps> = (props) => {
	const { t } = useTranslation();
	const { setItem, save, selectedCollections, setSelectedCollections } = useContext(
		ManageItemInCollectionContext
	);
	const [newCollectionId, setNewCollectionId] = useState('');
	const user = useSelector(selectUser);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (props.show) {
			setItem(props.itemId, props.itemType);
		}
	}, [props.itemId, props.itemType, props.show]);

	const handleSubmit = () => {
		save()
			.then(() => {
				if (props.onShowNotification) {
					props.onShowNotification({
						message: t('Your changes have been successfully saved.'),
						severity: 'success',
					});
				}
				props.onClose();
			})
			.catch(() => {
				if (props.onShowNotification) {
					props.onShowNotification({
						message: t('Something went wrong. Check your internet connection and try again.'),
						severity: 'error',
					});
				}
				props.onClose();
			});
		processingDataForEventTracking();
	};

	const processingDataForEventTracking = () => {
		if (user && props.itemData) {
			const eventProperties = getAmpliContentEventPayload(props.itemData, props.itemType);
			// Checking if an item has been removed from some collection
			if (selectedCollections.some((collection: ModalCollectionItem) => !collection.isSelected)) {
				const collectionContentRemovedPayload = {
					content: eventProperties.content,
				};
				services.track.ampliEventTrack({
					event: new CollectionContentRemoved(collectionContentRemovedPayload as any),
				});
			}

			// Checking if an item has been added to some collection
			if (selectedCollections.some((collection: ModalCollectionItem) => collection.isSelected)) {
				// Checking if the item was added to the new collection or in an already existing collection.
				// Required for getting "collection preexistence" field in amplitude events.
				const isSelectedNewCollection: boolean = newCollectionId?.length
					? selectedCollections.some(
							(collection: ModalCollectionItem) => collection.id === newCollectionId
						)
					: false;

				const contentAddedEventProperties = JSON.parse(JSON.stringify(eventProperties));
				if (isSelectedNewCollection) {
					contentAddedEventProperties.content[0]['collection preexistence'] = 'new collection';
				} else {
					contentAddedEventProperties.content[0]['collection preexistence'] = 'existing collection';
				}
				const searchId = getSearchIdWithFallback();
				if (searchId) {
					contentAddedEventProperties['search id'] = searchId;
				}

				services.track.ampliEventTrack({
					event: new CollectionContentAdded(contentAddedEventProperties as any),
				});
			}

			setSelectedCollections([]);
			setNewCollectionId('');
		}
	};

	return (
		<AddToCollectionModal
			open={props.show}
			onClose={props.onClose}
			onSave={handleSubmit}
			onSetNewCollectionId={(collectionId: string) => {
				setNewCollectionId(collectionId);
			}}
		/>
	);
};

export default ManageItemInCollectionModal;
